import { type FC, useMemo, useState } from 'react';

import { type EmojiItem, gitHubEmojis } from '@tiptap-pro/extension-emoji';
import cn from 'classnames';

import { PanelSearchInput } from '../panels/PanelSearchInput';

type UnicodeEmoji = EmojiItem & { emoji: string };
const EMOJI_LIST: UnicodeEmoji[] = gitHubEmojis.filter(
  (e): e is UnicodeEmoji =>
    Boolean(e.emoji) && !e.name.startsWith('regional_indicator_') && e.group?.trim() !== 'components',
);

const groupLabel = (rawGroup: string) => {
  if (!rawGroup) return 'Other';
  return rawGroup
    .split(/\s+/)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');
};

const EMOJI_GROUPS = (() => {
  const groups = new Map<string, UnicodeEmoji[]>();
  for (const emoji of EMOJI_LIST) {
    const rawGroup = emoji.group?.trim() ?? '';
    const group = groups.get(rawGroup);
    if (group) {
      group.push(emoji);
    } else {
      groups.set(rawGroup, [emoji]);
    }
  }
  return Array.from(groups, ([rawGroup, emojis]) => ({ group: groupLabel(rawGroup), emojis }));
})();

const matchesQuery = (emoji: UnicodeEmoji, q: string) => {
  if (emoji.name.toLowerCase().includes(q)) return true;
  if (emoji.shortcodes?.some((s) => s.toLowerCase().includes(q))) return true;
  if (emoji.tags?.some((t) => t.toLowerCase().includes(q))) return true;
  return false;
};

interface EmojiPickerLabels {
  searchPlaceholder: string;
  noResults: string;
}

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  labels: EmojiPickerLabels;
  autoFocus?: boolean;
}

export const EmojiPicker: FC<EmojiPickerProps> = ({ onSelect, labels, autoFocus }) => {
  const [query, setQuery] = useState('');
  const [activeGroup, setActiveGroup] = useState<string | null>(null);

  const sections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q) {
      const matches = EMOJI_LIST.filter((e) => matchesQuery(e, q));
      return matches.length ? [{ group: 'Results', emojis: matches }] : [];
    }
    if (activeGroup) {
      return EMOJI_GROUPS.filter((g) => g.group === activeGroup);
    }
    return EMOJI_GROUPS;
  }, [query, activeGroup]);

  return (
    <>
      <PanelSearchInput
        value={query}
        onChange={setQuery}
        placeholder={labels.searchPlaceholder}
        ariaLabel={labels.searchPlaceholder}
        autoFocus={autoFocus}
      />
      {!query.trim() && (
        <div className="flex gap-1 px-2 py-1 border-b border-black/5 overflow-x-auto">
          {EMOJI_GROUPS.map(({ group, emojis }) => {
            const isActive = activeGroup === group;
            return (
              <button
                key={group}
                type="button"
                onClick={() => setActiveGroup(isActive ? null : group)}
                className={cn('flex items-center justify-center size-8 rounded-md text-lg shrink-0', {
                  'bg-black/10': isActive,
                  'hover:bg-black/5': !isActive,
                })}
                aria-label={group}
                title={group}
              >
                {emojis[0]?.emoji ?? '?'}
              </button>
            );
          })}
        </div>
      )}
      <div className="max-h-72 overflow-y-auto p-2">
        {sections.length === 0 ? (
          <p className="text-sm text-black/50 text-center py-4">{labels.noResults}</p>
        ) : (
          sections.map((section) => (
            <div key={section.group} className="grid grid-cols-8 gap-1 mb-2 last:mb-0">
              {section.emojis.map((e) => (
                <button
                  key={e.name}
                  type="button"
                  onClick={() => onSelect(e.emoji)}
                  className="flex items-center justify-center size-8 rounded-md text-lg hover:bg-black/5"
                  aria-label={e.name}
                >
                  {e.emoji}
                </button>
              ))}
            </div>
          ))
        )}
      </div>
    </>
  );
};
